import { getAccessToken } from './axiosClient';
import { API_BASE_URL } from '../config/apiConfig';
import type { NotificationItem } from '../types/notification.types';

export type NotificationSocketEvent =
  | { event: 'notification_created'; data: NotificationItem }
  | { event: 'unread_count_updated'; data: { count: number } }
  | { event: 'pong'; data?: unknown };

type Handler = (event: NotificationSocketEvent) => void;

export type NotificationSocketHandle = {
  close: () => void;
  socket: WebSocket;
};

const toWebSocketUrl = (httpUrl: string): string => {
  if (httpUrl.startsWith('https://')) return httpUrl.replace(/^https:\/\//, 'wss://');
  if (httpUrl.startsWith('http://')) return httpUrl.replace(/^http:\/\//, 'ws://');
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  return httpUrl;
};

export const connectNotificationSocket = (onEvent: Handler): NotificationSocketHandle => {
  const token = getAccessToken();
  const base = toWebSocketUrl(API_BASE_URL);
  // The notifications WS is mounted at root (no /v1 prefix), matching backend main.py.
  const url = `${base}/ws/notifications${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  const socket = new WebSocket(url);

  socket.onmessage = (raw) => {
    try {
      const parsed = JSON.parse(raw.data) as NotificationSocketEvent;
      onEvent(parsed);
    } catch {
      /* ignore non-JSON frames */
    }
  };

  // Keep-alive ping every 25s to survive proxies that drop idle sockets.
  const pingTimer = window.setInterval(() => {
    if (socket.readyState === WebSocket.OPEN) {
      try { socket.send(JSON.stringify({ event: 'ping' })); } catch { /* noop */ }
    }
  }, 25000);

  socket.onclose = () => { window.clearInterval(pingTimer); };

  return {
    close: () => {
      window.clearInterval(pingTimer);
      try { socket.close(); } catch { /* noop */ }
    },
    socket,
  };
};

export const listenNotificationCreated = (handle: NotificationSocketHandle, cb: (notification: NotificationItem) => void) => {
  // Convenience filter — most callers just want the typed item.
  const original = handle.socket.onmessage;
  handle.socket.onmessage = (raw) => {
    try {
      const parsed = JSON.parse(raw.data) as NotificationSocketEvent;
      if (parsed.event === 'notification_created') cb(parsed.data);
    } catch { /* noop */ }
    if (original) original.call(handle.socket, raw);
  };
};

export const listenUnreadCountUpdated = (handle: NotificationSocketHandle, cb: (count: number) => void) => {
  const original = handle.socket.onmessage;
  handle.socket.onmessage = (raw) => {
    try {
      const parsed = JSON.parse(raw.data) as NotificationSocketEvent;
      if (parsed.event === 'unread_count_updated') cb(parsed.data.count);
    } catch { /* noop */ }
    if (original) original.call(handle.socket, raw);
  };
};
